import { Picker } from "@design-system/Picker/Picker.lib";
import { isLabelMatch } from "@design-system/Picker/PickerTree";
import { BankIcon, PlusIcon } from "@icons";
import type { buildSections } from "./PickerPlayground.lib";

/**
 * Composed sectioned list body for the static matrix: two titled groups
 * (select-all under `multiple`), optional footer, and a local filter for
 * searchable roots, which do not filter composed children. The live stage
 * uses the roots' data-driven `sections` prop instead.
 */
export const PickerSectionedList = ({
    sections,
    query,
    hasFooter,
    onFooterSelect,
}: {
    sections: ReturnType<typeof buildSections>;
    query: string;
    hasFooter: boolean;
    onFooterSelect: () => void;
}) => {
    const visibleSections = sections
        .map((section) => ({
            ...section,
            items: section.items.filter((item) => isLabelMatch(item, query)),
        }))
        .filter((section) => section.items.length > 0);

    return (
        <Picker.List>
            {visibleSections.map((section, index) => (
                <Picker.Section key={section.title}>
                    <Picker.SectionTitle
                        icon={index === 0 ? <BankIcon /> : undefined}
                        selectAll={{
                            values: section.items.map((item) => item.value),
                        }}
                    >
                        {section.title}
                    </Picker.SectionTitle>
                    {section.items.map((item) => (
                        <Picker.Item
                            key={item.value}
                            value={item.value}
                            description={item.description}
                            rightText={item.rightText}
                            disabled={item.disabled}
                            destructive={item.destructive}
                        >
                            {item.label}
                        </Picker.Item>
                    ))}
                </Picker.Section>
            ))}
            {visibleSections.length === 0 && <Picker.Empty />}
            {hasFooter && (
                <Picker.Footer>
                    <Picker.Item
                        leading={<PlusIcon />}
                        onSelect={onFooterSelect}
                    >
                        {query.trim() === ""
                            ? "Add account"
                            : `Add "${query.trim()}"`}
                    </Picker.Item>
                </Picker.Footer>
            )}
        </Picker.List>
    );
};
